import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import TableFilter from '../components/TableFilter';
import IncidentForm, { Incident } from '../components/IncidentForm';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';

const IncidentsPage: React.FC = () => {

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [filteredIncidents, setFilteredIncidents] = useState<Incident[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  // Master-Detail State
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  const fetchIncidents = useCallback(async () => {
    try {
      const response = await api.get('/incidents/');
      setIncidents(response.data);
      setFilteredIncidents(response.data);
    } catch (error) {
      console.error("Error fetching incidents", error);
    }
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = incidents;

    if (filters.description) {
      result = result.filter(i => i.description.toLowerCase().includes(filters.description.toLowerCase()));
    }
    if (filters.severity) {
      result = result.filter(i => i.severity === filters.severity);
    }
    if (filters.date) {
      result = result.filter(i => i.date.startsWith(filters.date));
    }

    setFilteredIncidents(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingIncident(null);
    fetchIncidents();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingIncident(null);
  };

  const handleEdit = (incident: Incident) => {
    setEditingIncident(incident);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta ocorrência?')) {
      try {
        await api.delete(`/incidents/${id}/`);
        fetchIncidents();
        if (selectedIncident?.id === id) setSelectedIncident(null);
      } catch (error) {
        console.error("Error deleting incident", error);
        alert('Erro ao excluir ocorrência.');
      }
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'Low': return <span className="badge badge-success">Baixa</span>;
      case 'Medium': return <span className="badge badge-warning">Média</span>;
      case 'High': return <span className="badge badge-danger">Alta</span>;
      default: return <span className="badge">{severity}</span>;
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Ocorrências</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Registro de incidentes e ações disciplinares</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Ocorrências</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Nova Ocorrência
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <IncidentForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            incidentToEdit={editingIncident}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'description', label: 'Descrição' },
            {
              key: 'severity', label: 'Gravidade', type: 'select', options: [
                { value: 'Low', label: 'Baixa' },
                { value: 'Medium', label: 'Média' },
                { value: 'High', label: 'Alta' }
              ]
            },
            { key: 'date', label: 'Data', type: 'date' }
          ]}
          onFilter={handleFilter}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal 
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Incident"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Turmas</th>
                <th>Gravidade</th>
                <th>Descrição</th>
                <th>Responsável</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredIncidents.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhuma ocorrência registrada.
                  </td>
                </tr>
              ) : (
                filteredIncidents.map((incident) => (
                  <tr
                    key={incident.id}
                    onClick={() => setSelectedIncident(incident)}
                    className={selectedIncident?.id === incident.id ? 'selected' : ''}
                  >
                    <td>{new Date(incident.date).toLocaleDateString()}</td>
                    <td>
                      {incident.school_classes && incident.school_classes.length > 0 ? (
                        incident.school_classes.map((cls) => (
                          <span key={cls.id} className="badge badge-info" style={{ marginRight: '5px' }}>
                            {cls.name}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: 'var(--text-light)' }}>-</span>
                      )}
                    </td>
                    <td>{getSeverityBadge(incident.severity)}</td>
                    <td style={{ maxWidth: '300px' }}>{incident.description}</td>
                    <td>{incident.responsible_name || '-'}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButtons
                        onReport={() => incident.id && openRowReport(incident.id)}
                        onEdit={() => handleEdit(incident)}
                        onDelete={() => incident.id && handleDelete(incident.id)}
                        deletePermission="delete_incident"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedIncident && (
          <DetailsPanel title="Detalhes da Ocorrência" onClose={() => setSelectedIncident(null)}>
            <div className="details-row">
              <div className="details-label">Data</div>
              <div className="details-value">{new Date(selectedIncident.date).toLocaleDateString()}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Gravidade</div>
              <div className="details-value">{getSeverityBadge(selectedIncident.severity)}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável</div>
              <div className="details-value">{selectedIncident.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Turmas Envolvidas</div>
              <div className="details-value">
                {selectedIncident.school_classes && selectedIncident.school_classes.length > 0 ? (
                  selectedIncident.school_classes.map((cls) => (
                    <span key={cls.id} className="badge badge-info" style={{ marginRight: '5px', marginBottom: '5px' }}>
                      {cls.name}
                    </span>
                  ))
                ) : (
                  <span style={{ color: 'var(--text-light)' }}>-</span>
                )}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Descrição</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedIncident.description}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Ações Tomadas</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedIncident.actions_taken || 'Nenhuma ação registrada.'}
              </div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default IncidentsPage;
